import type {ExperienceGateMode, ExperienceGateVariant} from 'common/experiments';
import {track} from 'iq-tracking';

// The locked gate ran as A/B experiment `guide_gate_signup_cta` between 2026-09-01 and
// 2026-09-14; the signup-first arm won (5.9% -> 11.6% signups, p = 0.006) and is now the
// only card anonymous visitors see. The variant field stays on the payload so the
// readout SQL keeps parsing, and this sentinel separates post-ship rows from the
// control/experiment rows recorded during the experiment window.
const SHIPPED_GATE_VARIANT = 'shipped';

// Called from share_button (once per provider — twitter, linkedin, reddit).
export const trackGuideShared = (provider: string, slug: string): Promise<void> =>
  track({
    event: 'guide_shared',
    action: 'click',
    component: 'button',
    content_type: 'guide',
    content_identifier: slug,
    extra: {extra_data: JSON.stringify({provider})},
  });

// Called from hero_actions.tsx — shared by both the CIG and position guide headers.
export const trackGuideApplyClicked = (identifier: string): Promise<void> =>
  track({
    event: 'guide_apply_clicked',
    action: 'click',
    component: 'header',
    content_type: 'guide',
    content_identifier: identifier,
  });

// Called from faq_block.tsx when a question is opened (not on close, so this counts
// genuine reads, not the toggle-shut interaction).
export const trackFaqExpanded = (companySlug: string, question: string): Promise<void> =>
  track({
    event: 'company_guide_faq_expanded',
    action: 'expand',
    component: 'module',
    content_type: 'guide',
    content_identifier: companySlug,
    extra: {extra_data: JSON.stringify({question})},
  });

// Called from topics_distribution.tsx's role-domain filter tabs.
export const trackTopicsFilterChanged = (companySlug: string, filter: string): Promise<void> =>
  track({
    event: 'company_guide_topics_filter_changed',
    action: 'click',
    component: 'module',
    content_type: 'guide',
    content_identifier: companySlug,
    extra: {extra_data: JSON.stringify({filter})},
  });

// Called from recent_experiences.tsx's paginator.
export const trackRecentExperiencesPageChanged = (companySlug: string, page: number): Promise<void> =>
  track({
    event: 'company_guide_recent_experiences_page_changed',
    action: 'click',
    component: 'module',
    content_type: 'guide',
    content_identifier: companySlug,
    extra: {extra_data: JSON.stringify({page})},
  });

// Called from recent_experience_card.tsx — mirrors the feed card's is_locked payload
// so unlock attempts and in-place expands can be told apart in the same event.
export const trackRecentExperienceCardClicked = (
  companySlug: string,
  experienceId: string,
  isLocked: boolean,
): Promise<void> =>
  track({
    event: 'company_guide_recent_experience_clicked',
    action: 'click',
    component: 'module',
    content_type: 'interview',
    content_identifier: companySlug,
    extra: {extra_data: JSON.stringify({experience_id: experienceId, locked: isLocked})},
  });

// Called once from locked_guide_gate.tsx when the full-page gate renders over a
// locked guide. `trigger` is the gate mode (anonymous vs. logged-in free); it is the
// exposure denominator for anything measured on this surface, so it fires for both
// modes.
export const trackGuidePaywallViewed = ({
  gateMode,
  slug,
}: {
  gateMode: 'public' | 'free';
  slug: string;
}): Promise<void> =>
  track({
    event: 'guide_paywall_viewed',
    action: 'view',
    component: 'barrier',
    content_type: 'guide',
    content_identifier: slug,
    trigger: gateMode,
    extra: {extra_data: JSON.stringify({variant: SHIPPED_GATE_VARIANT})},
  });

// Called from locked_guide_gate.tsx when the gate's signup CTA is clicked. Anonymous
// visitors are the only ones who see that CTA.
export const trackGuideSignupClicked = ({slug}: {slug: string}): Promise<void> =>
  track({
    event: 'guide_signup_clicked',
    action: 'click',
    component: 'barrier',
    content_type: 'guide',
    content_identifier: slug,
    extra: {extra_data: JSON.stringify({variant: SHIPPED_GATE_VARIANT})},
  });

// Called from experiences_paywall.tsx — the inline paywall shown inside a
// partially-unlocked guide, which is a different surface from the full-page gate above
// and is not part of any experiment.
export const trackGuideInlinePaywallViewed = ({slug}: {slug: string}): Promise<void> =>
  track({
    event: 'guide_inline_paywall_viewed',
    action: 'view',
    component: 'barrier',
    content_type: 'guide',
    content_identifier: slug,
  });

// guide_anon_experience_gate_v1 (experiences paywall on a partially-unlocked guide).
// Fired once per page view from interview_experiences_tab.tsx for both arms and both gate
// modes, so it is the exposure denominator; `trigger` carries the gate mode.
// `locked_count` is how many reports the visitor cannot read on this view.
export const trackGuideExperiencePaywallViewed = ({
  slug,
  variant,
  gateMode,
  lockedCount,
}: {
  slug: string;
  variant: ExperienceGateVariant;
  gateMode: ExperienceGateMode;
  lockedCount: number;
}): Promise<void> =>
  track({
    event: 'guide_experience_paywall_viewed',
    action: 'view',
    component: 'barrier',
    content_type: 'guide',
    content_identifier: slug,
    trigger: gateMode,
    extra: {
      extra_data: JSON.stringify({variant, gate_mode: gateMode, locked_count: lockedCount}),
    },
  });

// Called from experience_gate_card.tsx when an anonymous visitor in the experiment arm
// clicks "Sign up free to read a report". Control has no such CTA.
export const trackGuideExperienceSignupClicked = ({
  slug,
  variant,
}: {
  slug: string;
  variant: ExperienceGateVariant;
}): Promise<void> =>
  track({
    event: 'guide_experience_signup_clicked',
    action: 'click',
    component: 'barrier',
    content_type: 'guide',
    content_identifier: slug,
    extra: {extra_data: JSON.stringify({variant})},
  });

// Called from both experiences paywall cards on any subscribe/pricing link, in both arms.
// The existing `subscribe_clicked` event still fires alongside it where it did before.
export const trackGuideExperienceSubscribeClicked = ({
  slug,
  variant,
  gateMode,
}: {
  slug: string;
  variant: ExperienceGateVariant;
  gateMode: ExperienceGateMode;
}): Promise<void> =>
  track({
    event: 'guide_experience_subscribe_clicked',
    action: 'click',
    component: 'barrier',
    content_type: 'guide',
    content_identifier: slug,
    trigger: gateMode,
    extra: {extra_data: JSON.stringify({variant, gate_mode: gateMode})},
  });

// Called from practice_with_ai_cta.tsx — "Practice this interview with AI" on a position guide.
export const trackPracticeWithAIClicked = (guideSlug: string): Promise<void> =>
  track({
    event: 'ai_mock_interview_cta_click',
    action: 'click',
    component: 'button',
    content_type: 'interview_guide',
    content_identifier: guideSlug,
  });

// Called from guide_feedback.tsx when a logged-in reader votes on "Is this guide helpful?".
export const trackGuideFeedbackVote = ({
  slug,
  vote,
}: {
  slug: string;
  vote: 1 | -1;
}): Promise<void> =>
  track({
    event: 'guide_feedback_vote',
    action: 'click',
    component: 'button',
    content_type: 'guide',
    content_identifier: slug,
    extra: {extra_data: JSON.stringify({vote, slug})},
  });

// Called from guide_feedback_dialog.tsx when the reason box is submitted.
export const trackGuideFeedbackSubmitted = ({
  slug,
  reason,
}: {
  slug: string;
  reason: string;
}): Promise<void> =>
  track({
    event: 'guide_feedback_submitted',
    action: 'submit',
    component: 'modal',
    content_type: 'guide',
    content_identifier: slug,
    extra: {extra_data: JSON.stringify({reason, slug})},
  });
